import {useCallback, useEffect, useMemo, useState} from 'react';
import * as GQL from '../generated/graphql';
import {RunSignature} from '../types/run';
import {FILE_QUERY} from '../graphql/media';
import {useApolloClient} from '../state/hooks';
import {RequireSome} from '../types/base';
import {propagateErrorsContext} from './errors';

interface RetrySpec {
  maxRetries: number;
  calcDelay: (n: number) => number;
}

const STANDARD_RETRY: RetrySpec = {
  maxRetries: 10,
  calcDelay: (n: number) => 500 * Math.pow(1.51337, n),
};

export interface RetryRequest {
  abort: () => void;
}

// Makes an http request with retries included
// options:
//   onError(e, retry) - called every time a request returns a failure. This
//     allows the caller to check the error and decide to retry or perform
//     a different action
//   onFailure - called if the request fails more than the max limit
//   onSuccess - called when the request returns successful
//   retry - A spec to control the way the request is retried
//   method - defaults to GET
export function fetchWithRetry(
  url: string,
  options: {
    onError?: (e: any, retry: () => void) => void;
    onSuccess?: (response: any) => void;
    onFailure?: CallableFunction;
    retry?: RetrySpec;
    method?: string;
    responseType?: XMLHttpRequestResponseType | 'file';
  }
): RetryRequest {
  const retrySpec =
    options.retry === undefined ? STANDARD_RETRY : options.retry;

  const {onError, onSuccess, onFailure} = options;

  // Keep track of the latest retry timeout
  let timerID: number;
  let xhr: XMLHttpRequest;

  // We hold the retry count as a local closure around the event.
  // Doing this instead of holding it in state allows us to create the
  // event listeners once and only recreated them on source change
  let retryCount = 0;

  // Use xhr instead of fetch because abort support for fetch is currently
  // experimental - Oct 24, 2019
  const makeRequest = () => {
    xhr = new XMLHttpRequest();

    // Create a new dom element so we don't affect the handlers on the original element
    xhr.open(options.method || 'get', url);
    if (options.responseType === 'file') {
      xhr.responseType = 'blob';
    } else if (options.responseType) {
      xhr.responseType = options.responseType;
    }

    // Attach an event for retries
    xhr.onerror = (e: any) => {
      if (retryCount < retrySpec.maxRetries) {
        // Create a retry function so we can allow the caller to
        // control execution of the callback function
        const retry = () => {
          timerID = window.setTimeout(() => {
            retryCount = retryCount + 1;

            // Setting the source triggers a refresh
            makeRequest();
          }, retrySpec.calcDelay(retryCount));
        };

        // If the user provides an on error method allow them to control
        // the retry otherwise. Retry immediately
        if (onError) {
          onError(e, retry);
          retry();
        }
      } else {
        // Otherwise fail loading and call the error message
        if (onFailure) {
          onFailure();
        }
      }
    };

    xhr.onload = response => {
      if (onSuccess) {
        const res =
          options.responseType === 'file'
            ? blobToFile(xhr.response, url)
            : xhr.response;

        onSuccess(res);
      }
    };

    // Set the source and trigger a request
    xhr.send();
  };

  makeRequest();

  // Reset to initial state
  return {
    abort: () => {
      if (timerID) {
        window.clearTimeout(timerID);
      }
      if (xhr) {
        xhr.abort();
      }
    },
  };
}

export async function getUrlWithRetry(url: string) {
  return new Promise<string>((resolve, reject) => {
    fetchWithRetry(url, {
      onSuccess: response => {
        resolve(response);
      },
      onError: () => {
        reject('too many request failurs for: ' + url);
      },
    });
  });
}

interface GetDirectUrlArgs {
  entityName: string;
  projectName: string;
  runName: string;
  filename: string;
}

export type FileInfo = RequireSome<GQL.File, 'directUrl'>;

// Cache the file urls for N
export const fetchFileInfo = (
  client: ReturnType<typeof useApolloClient>,
  args: GetDirectUrlArgs
): Promise<FileInfo | null> => {
  const request = client
    .query({
      query: FILE_QUERY,
      fetchPolicy: 'no-cache', // google cloud store urls expire in 60 seconds, so we need to disable apollo caching
      variables: args,
      context: propagateErrorsContext(),
    })
    .then((response: any) => {
      const {project} = response.data;
      if (
        project &&
        project.run &&
        project.run.files &&
        project.run.files.edges &&
        project.run.files.edges[0] &&
        project.run.files.edges[0].node &&
        project.run.files.edges[0].node.directUrl
      ) {
        return project.run.files.edges[0].node;
      }
    });

  return request;
};

// This function triggers a file download in the user's browser.
//
// It must be called in response to a user action in the browser, e.g. click
export const downloadFile = (
  runSignature: RunSignature,
  filename: string,
  client: ReturnType<typeof useApolloClient>
) => {
  fetchFileInfo(client, {...runSignature, filename}).then(f => {
    if (f) {
      window.open(f.directUrl);
    } else {
      throw new Error(
        `Failed to fetch url for file download: ${{...runSignature, filename}}`
      );
    }
  });

  return;
};

export const useDownloadFile = () => {
  const client = useApolloClient();
  return useCallback(
    (runSignature: RunSignature, filename: string) => {
      fetchFileInfo(client, {...runSignature, filename}).then(f => {
        if (f) {
          window.open(f.directUrl);
        } else {
          throw new Error(
            `Failed to fetch url for file download: ${{
              ...runSignature,
              filename,
            }}`
          );
        }
      });
    },
    [client]
  );
};

// Use load file is a react hook for loading file's from our file store
// It handles the fetching of the signed urls for cloud storage,
// supports fallback for missing files, and request retries for failed
// requests

//
// useLoadFile:
// - options
//   fallback:  A function that is called when the requested
//              resource returns a 404
//   onSuccess: A function that is called when the file is successfully
//              fetched
//   onFailure: A function that is called after all retries have failed
//   retry:     A retry spec to control how retries operate
//   skip:      When true a request will not be made
export function useLoadFile(
  {entityName, projectName, runName}: RunSignature,
  filePath: string,
  options: {
    fallback?: () => void;
    onSuccess?: (response: any, metadata: GQL.File) => void;
    onFailure?: () => void;
    retry?: RetrySpec;
    skip?: boolean;
    responseType?: XMLHttpRequestResponseType | 'file';
  }
) {
  const client = useApolloClient();
  const [loading, setLoading] = useState(true);

  const {onSuccess, onFailure, fallback, retry, responseType, skip} = options;

  // Creat a memoized version of the runSignature, so it will only trigger
  // hook updates when its contents change
  const runSignature = useMemo(
    () => ({
      entityName,
      projectName,
      runName,
    }),
    [entityName, projectName, runName]
  );

  useEffect(() => {
    if (skip) {
      return; // Do nothing
    }
    // On windows, prior to CLI 0.8.26, we were saving '\' in media paths
    // (in media file references that are saved in history or summary). We
    // convert them back here. This isn't just a blind replace of all
    // backslashes with forward-slashes, because we don't want to do that
    // if the path was set on a non-windows machine. We don't have an easy
    // way to detect if the data was logged from windows here, so we just
    // fix up media paths (which is the only case that needs to be fixed
    // anyway).
    const filename = filePath.startsWith('media\\')
      ? filePath.replace(/\\/g, '/')
      : filePath;

    let retryReq: RetryRequest | undefined;

    if (runSignature) {
      fetchFileInfo(client, {...runSignature, filename}).then(file => {
        // If the file is empty is hasn't uploaded yet we
        // call the fallback method,
        //
        // This allows the caller to perform actions like
        // rollback to an older image, display an error message, etc
        // TODO: in dev mode / CI we don't always have sizeBytes for reasons unclear
        if (fallback && (file == null || file.sizeBytes === 0)) {
          fallback();
        } else if (file != null) {
          //  Otherwise we request the file with retries
          retryReq = fetchWithRetry(file.directUrl, {
            responseType,
            retry,
            onFailure: () => {
              if (onFailure) {
                onFailure();
              }
            },
            onSuccess: res => {
              if (onSuccess) {
                onSuccess(res, file);
              }
              setLoading(false);
            },
          });
        }
      });
    }

    return () => {
      if (retryReq) {
        retryReq.abort();
      }
    };
  }, [
    client,
    runSignature,
    fallback,
    onSuccess,
    onFailure,
    retry,
    skip,
    filePath,
    responseType,
  ]);

  // Indicate loading finished for skipped hooks
  return skip ? false : loading;
}

export function useLoadFileWithFallback(
  {entityName, projectName, runName}: RunSignature,
  filePath: string,
  options: {
    fallback?: () => void;
    onSuccess?: (response: any, metadata: GQL.File) => void;
    onFailure?: () => void;
    retry?: RetrySpec;
    skip?: boolean;
    responseType?: XMLHttpRequestResponseType | 'file';
  }
) {
  return useLoadFile({entityName, projectName, runName}, filePath, options);
}

const blobToFile = (theBlob: Blob, fileName: string): File | Blob => {
  try {
    return new File([theBlob], fileName);
  } catch {
    return theBlob;
  }
};

export function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.setAttribute('download', filename);
  a.click();
}
